<template>
	<form
		action=""
		@click="form.onFormFocus"
	>
		<v-text-field
			v-model="form.name.model"
			:label="`${$t('Your name')}*`"
			variant="underlined"
			class="mb-2"
			:error="!form.name.$valid && form.name.$touched"
			:error-messages="[
				...(form.name.$errors.required && form.name.$touched ? [$t('This field is required')] : []),
				...(form.name.$errors.request ? [form.name.$errors.request] : []),
			]"
			@blur="form.name.onFieldBlur"
			@keyup.enter="submit"
		/>

		<v-text-field
			v-model="form.email.model"
			:label="`${$t('E-mail')}*`"
			variant="underlined"
			class="mb-2"
			:error="!form.email.$valid && form.email.$touched"
			:error-messages="[
				...(form.email.$errors.required && form.email.$touched ? [$t('This field is required')] : []),
				...(form.email.$errors.request ? [form.email.$errors.request] : []),
			]"
			@blur="form.email.onFieldBlur"
			@keyup.enter="submit"
		/>

		<v-text-field
			v-model="form.password.model"
			:label="`${$t('Password')}*`"
			variant="underlined"
			class="mb-2"
			type="password"
			:error="!form.password.$valid && form.password.$touched"
			:error-messages="[
				...(form.password.$errors.required && form.password.$touched ? [$t('This field is required')] : []),
				...(form.password.$errors.request ? [form.password.$errors.request] : []),
			]"
			@blur="form.password.onFieldBlur"
			@keyup.enter="submit"
		/>

		<v-text-field
			v-model="form.password2.model"
			:label="`${$t('Password confirmation')}*`"
			variant="underlined"
			class="mb-2"
			type="password"
			:error="!form.password2.$valid && form.password2.$touched"
			:error-messages="[
				...(form.password2.$errors.required && form.password2.$touched ? [$t('This field is required')] : []),
				...(form.password2.$errors.passwordEqual && form.password2.$touched ? [$t('Passwords dont match')] : []),
				...(form.password2.$errors.request ? [form.password2.$errors.request] : []),
			]"
			@blur="form.password2.onFieldBlur"
			@keyup.enter="submit"
		/>

		<ao-checkbox
			v-model="acceptedTerms"
			isLabelMultiRow
			class="text-caption mb-3"
		>
			<span v-html="$t('By clicking the Register, you accept the terms of the User Agreement')"/>
		</ao-checkbox>

		<div
			v-if="form.formErrors.length"
			class="my-2 px-3 py-3"
			style="background: #ffdfdd; color: #ea4c42"
		>
			<p
				v-for="error in form.formErrors"
				:key="error"
			>
				{{ error }}
			</p>
		</div>

		<div class="pt-3">
			<v-btn
				color="primary"
				:loading="form.$loading"
				:disabled="isDisabledRegister"
				@click="submit"
			>
				{{ $t('Register') }}
			</v-btn>
		</div>
	</form>
</template>

<script setup>
import { ref, computed, toRefs, watch } from 'vue'

import FormManager from '@/services/formManager/formManagerService'
import { required, email } from '@/services/formManager/formValidators'

const props = defineProps({
	requestLoading: {
		type: Boolean,
		default: () => false,
	},
	extraFieldsErrors: {
		type: Object,
		default: () => {},
	},
	isVisible: {
		type: Boolean,
		default: () => false,
	},
})

const emit = defineEmits({
	submit: null,
})

const { requestLoading, extraFieldsErrors, isVisible } = toRefs(props)

const form = FormManager({
	fields: {
		name: {
			validators: {
				required: (value) => required(value),
			},
			defaultValue: () => '',
		},
		email: {
			validators: {
				required: (value) => required(value),
				email: (value) => email(value),
			},
			defaultValue: () => '',
		},
		password: {
			validators: {
				required: (value) => required(value),
			},
			defaultValue: () => '',
		},
		password2: {
			validators: {
				required: (value) => required(value),
			},
			defaultValue: () => '',
		},
	},
	dependencies: {
		password: {
			password2: {
				type: 'modelChange',
				callback: (parentField, field) => {
					const validatorName = 'passwordEqual'
					const validatorCallback = () => parentField.model === field.model

					if (field.validators[validatorName]) {
						field.removeValidator([validatorName])
					}

					field.addValidator([[validatorName, validatorCallback]])
				},
			},
		},
	},
})

const acceptedTerms = ref(false)

const isDisabledRegister = computed(() => {
	return acceptedTerms.value ? false : true
})

const submit = async () => {
	const isValid = await form.validateForm()

	if (!isValid) return

	emit('submit', form.getFormValues())
}

watch(
	() => requestLoading.value,
	() => form.setFormLoading(requestLoading.value)
)

watch(
	() => extraFieldsErrors.value,
	() =>
		form.setFormErrors(
			Object.fromEntries(
				Object.entries(extraFieldsErrors.value).map(([fieldName, errors]) => [fieldName, { request: errors }])
			),
			{ touch: true }
		)
)

watch(
	() => isVisible.value,
	() => !isVisible.value && form.resetForm()
)
</script>
